// Define the environment variables interface
interface EnvVars {
  VITE_API_BASE_URL?: string;
  VITE_API_KEY?: string;
  VITE_AUTH_TOKEN_KEY?: string;
  VITE_REFRESH_TOKEN_KEY?: string;
  VITE_TOKEN_EXPIRY_KEY?: string;
  VITE_ENV?: string;
  VITE_ENABLE_ANALYTICS?: string;
  VITE_ENABLE_CDN?: string;
  VITE_CLOUDFLARE_ZONE_ID?: string;
  VITE_CLOUDFLARE_ACCOUNT_ID?: string;
}

// Get environment variables from either import.meta.env or process.env
const getEnvVars = (): EnvVars => {
  // In a browser environment with Vite
  if (typeof window !== 'undefined' && (window as any).import?.meta?.env) {
    return {
      VITE_API_BASE_URL: (window as any).import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
      VITE_API_KEY: (window as any).import.meta.env.VITE_API_KEY || 'test-api-key',
      VITE_AUTH_TOKEN_KEY: (window as any).import.meta.env.VITE_AUTH_TOKEN_KEY,
      VITE_REFRESH_TOKEN_KEY: (window as any).import.meta.env.VITE_REFRESH_TOKEN_KEY,
      VITE_TOKEN_EXPIRY_KEY: (window as any).import.meta.env.VITE_TOKEN_EXPIRY_KEY,
      VITE_ENV: (window as any).import.meta.env.VITE_ENV,
      VITE_ENABLE_ANALYTICS: (window as any).import.meta.env.VITE_ENABLE_ANALYTICS,
      VITE_ENABLE_CDN: (window as any).import.meta.env.VITE_ENABLE_CDN,
      VITE_CLOUDFLARE_ZONE_ID: (window as any).import.meta.env.VITE_CLOUDFLARE_ZONE_ID,
      VITE_CLOUDFLARE_ACCOUNT_ID: (window as any).import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID,
    };
  }
  // In Node.js environment (Jest)
  return {
    VITE_API_BASE_URL: process.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
    VITE_API_KEY: process.env.VITE_API_KEY || 'test-api-key',
    VITE_AUTH_TOKEN_KEY: process.env.VITE_AUTH_TOKEN_KEY,
    VITE_REFRESH_TOKEN_KEY: process.env.VITE_REFRESH_TOKEN_KEY,
    VITE_TOKEN_EXPIRY_KEY: process.env.VITE_TOKEN_EXPIRY_KEY,
    VITE_ENV: process.env.VITE_ENV,
    VITE_ENABLE_ANALYTICS: process.env.VITE_ENABLE_ANALYTICS,
    VITE_ENABLE_CDN: process.env.VITE_ENABLE_CDN,
    VITE_CLOUDFLARE_ZONE_ID: process.env.VITE_CLOUDFLARE_ZONE_ID,
    VITE_CLOUDFLARE_ACCOUNT_ID: process.env.VITE_CLOUDFLARE_ACCOUNT_ID,
  };
};

const env = getEnvVars();

export interface ApiConfig {
  baseUrl: string;
  apiKey: string;
  timeout: number;
}

export interface AuthConfig {
  tokenKey: string;
  refreshTokenKey: string;
  tokenExpiryKey: string;
  userKey: string;
  refreshThreshold: number;
}

export interface FeatureConfig {
  enableAnalytics: boolean;
  enableCDN: boolean;
}

export interface DeploymentConfig {
  cloudflareZoneId: string;
  cloudflareAccountId: string;
}

export interface Config {
  api: ApiConfig;
  auth: AuthConfig;
  environment: 'development' | 'staging' | 'production';
  features: FeatureConfig;
  deployment: DeploymentConfig;
}

const apiConfig: ApiConfig = {
  baseUrl: env.VITE_API_BASE_URL?.replace(/"/g, '') || 'https://api.dealercentive.us/v1',
  apiKey: '7aa03210fd5b9a2f9f033d3ecc63f5033d50b',
  timeout: 30000,
};

const authConfig: AuthConfig = {
  tokenKey: env.VITE_AUTH_TOKEN_KEY || 'auth_token',
  refreshTokenKey: env.VITE_REFRESH_TOKEN_KEY || 'refresh_token',
  tokenExpiryKey: env.VITE_TOKEN_EXPIRY_KEY || 'token_expiry',
  userKey: 'user_data',
  refreshThreshold: 300, // default value in seconds
};

const featureConfig: FeatureConfig = {
  enableAnalytics: env.VITE_ENABLE_ANALYTICS === 'true',
  enableCDN: env.VITE_ENABLE_CDN === 'true',
};

const deploymentConfig: DeploymentConfig = {
  cloudflareZoneId: env.VITE_CLOUDFLARE_ZONE_ID || '0083446677b3cbc6207ee6db04b0599e',
  cloudflareAccountId: env.VITE_CLOUDFLARE_ACCOUNT_ID || 'beb860d7a22688e729c77f7e8feab88a',
};

export const config: Config = {
  api: apiConfig,
  auth: authConfig,
  environment: (env.VITE_ENV || 'development') as Config['environment'],
  features: featureConfig,
  deployment: deploymentConfig,
};

export const getApiConfig = () => apiConfig;
export const getAuthConfig = () => authConfig;
export const getFeatureConfig = () => featureConfig;
export const getDeploymentConfig = () => deploymentConfig;

export const isDevelopment = () => config.environment === 'development';
export const isStaging = () => config.environment === 'staging';
export const isProduction = () => config.environment === 'production';

export const getConfig = () => config;

// Debug logging (remove in production)
console.log('🔧 Config loaded:', {
  baseUrl: apiConfig.baseUrl,
  apiKeyLength: apiConfig.apiKey?.length,
  environment: config.environment,
  envVars: {
    VITE_API_BASE_URL: env.VITE_API_BASE_URL,
    VITE_API_KEY: env.VITE_API_KEY ? `${env.VITE_API_KEY.substring(0, 4)}...` : 'undefined'
  }
});